import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import css from './SymptomsAreReal.module.css';
import symptomImg from '../../../assets/landing/symptom-img.svg';
import shape1 from '../../../assets/landing/symptom-shape1.png';
import shape2 from '../../../assets/landing/symptom-shape2.png';
import { SYMPTOM_BLOG_LINKS, fallbackBlogUrl } from './symptomBlogLinks';
import { isRtlLocale } from '../../../util/genericHelpers';

function SymptomsAreReal(props) {
  const intl = useIntl();
  const { viewport } = props;
  const isMobileLayout = viewport?.width > 0 && viewport?.width < 768;
  const title = intl.formatMessage({ id: 'SymptomsAreReal.title' });

  const locale = intl.locale || 'en';
  const isGreekLocale = locale === 'el';
  const rtl = isRtlLocale(locale);

  const symptoms = useMemo(
    () => ({
      left: [
        { key: 'lowEnergy', messageId: 'SymptomsAreReal.symptom.lowEnergy' },
        { key: 'insomnia', messageId: 'SymptomsAreReal.symptom.insomnia' },
        { key: 'brainFog', messageId: 'SymptomsAreReal.symptom.brainFog' },
        { key: 'moodSwings', messageId: 'SymptomsAreReal.symptom.moodSwings' },
        { key: 'headaches', messageId: 'SymptomsAreReal.symptom.headaches' },
        { key: 'skinDryness', messageId: 'SymptomsAreReal.symptom.skinDryness' },
        { key: 'jointPain', messageId: 'SymptomsAreReal.symptom.jointPain' },
      ],
      right: [
        { key: 'hotFlashes', messageId: 'SymptomsAreReal.symptom.hotFlashes' },
        { key: 'anxiety', messageId: 'SymptomsAreReal.symptom.anxiety' },
        { key: 'bloating', messageId: 'SymptomsAreReal.symptom.bloating' },
        { key: 'lowLibido', messageId: 'SymptomsAreReal.symptom.lowLibido' },
        { key: 'weightGain', messageId: 'SymptomsAreReal.symptom.weightGain' },
        { key: 'uterine', messageId: 'SymptomsAreReal.symptom.uterine' },
        { key: 'irregularPeriod', messageId: 'SymptomsAreReal.symptom.irregularPeriod' },
        { key: 'vaginalDryness', messageId: 'SymptomsAreReal.symptom.vaginalDryness' },
      ],
    }),
    []
  );

  const resolveUrlForSymptom = key => {
    const mapping = SYMPTOM_BLOG_LINKS[key];
    if (!mapping) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`Missing blog link mapping for symptom key: ${key}`);
      }
      return fallbackBlogUrl(locale);
    }
    return isGreekLocale ? mapping.greekUrl : mapping.englishUrl;
  };

  const renderPill = symptom => {
    const href = resolveUrlForSymptom(symptom.key);
    return (
      <a
        key={symptom.key}
        className={css.pill}
        href={href}
        rel="noopener noreferrer"
        target="_self"
      >
        {intl.formatMessage({ id: symptom.messageId })}
      </a>
    );
  };

  return (
    <section className={css.symptomsSection} dir={rtl ? 'rtl' : 'ltr'}>
      <span className={css.shape1}>
        <img src={shape1} alt="" />
      </span>{' '}
      <span className={css.shape2}>
        <img src={shape2} alt="" />
      </span>
      <div className={css.inner}>
        <header className={css.header}>
          <h2 className={css.title} dangerouslySetInnerHTML={{ __html: title }} />
          <p className={css.subtitle}>{intl.formatMessage({ id: 'SymptomsAreReal.subtitle' })}</p>
        </header>

        <div className={css.content}>
          {/* Left symptoms */}
          <div className={css.pillColumn}>{symptoms.left.map(renderPill)}</div>

          {/* Center illustration placeholder */}
          <div className={css.illustrationWrap}>
            <img src={symptomImg} alt="" />
          </div>

          {/* Right symptoms */}
          <div className={css.pillColumn}>{symptoms.right.map(renderPill)}</div>
        </div>
      </div>
      <div className={css.curveImg}>
        <svg
          width="393"
          height="1558"
          viewBox="0 0 393 1558"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M-984.829 1249.79C-842.461 1337.08 -464.404 1560.22 126.786 1557.82C545.875 1556.18 972.728 1429.2 1237.6 1211.1C1346.45 1121.42 1427.5 1018.39 1482.2 910.727C1571.49 734.598 1588.32 538.101 1473.3 373.733C1358.26 209.279 1094.06 86.5392 812.448 102.137C606.858 113.488 411.653 193.484 207.002 185.173C-30.9269 175.46 -213.996 50.2012 -443.517 12.2066C-699.331 -30.0845 -977.256 42.8704 -1194.75 148.628C-1270.17 185.337 -1342.3 226.907 -1392.21 278.82C-1485.75 375.967 -1496.36 469.998 -1500.52 514.769C-1517.81 698.459 -1374.43 1010.98 -984.829 1249.79Z"
            fill="#FDF4EC"
            className={css.curveFill}
          />
        </svg>
      </div>
    </section>
  );
}

export default SymptomsAreReal;
